/**
 * SmartFix Image Compressor Utility
 * Automatically resizes & compresses any image format to <= 1MB (1,048,576 bytes).
 * Supports JPEG, PNG, WebP, GIF, BMP, HEIC, HEIF, SVG, TIFF, AVIF, ICO, etc.
 */

export const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024; // 1MB

export const SUPPORTED_IMAGE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif', 'svg', 'tif', 'tiff', 'avif', 'ico',
  'jfif', 'pjpeg', 'pjp', 'raw', 'cr2', 'nef', 'arw', 'dng', 'pdf'
];

export const ACCEPT_ALL_IMAGES = '*/*';

/**
 * Checks if a given file is an image or valid attachment.
 * @param {File|Blob} file 
 * @returns {boolean}
 */
export function isImageFile(file) {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) {
    return true;
  }
  const name = file.name || '';
  const ext = name.split('.').pop().toLowerCase();
  return SUPPORTED_IMAGE_EXTENSIONS.includes(ext) || ext !== '';
}

/**
 * Checks if a file is in HEIC or HEIF format.
 * @param {File|Blob} file 
 * @returns {boolean}
 */
export function isHeicFile(file) {
  if (!file) return false;
  const name = (file.name || '').toLowerCase();
  const ext = name.split('.').pop();
  const type = (file.type || '').toLowerCase();
  return ext === 'heic' || ext === 'heif' || type.includes('heic') || type.includes('heif');
}

/**
 * Converts a HEIC/HEIF file into a standard JPEG File using heic2any.
 * Enables all desktop and mobile browsers to display the photo as a normal image preview.
 * @param {File|Blob} file 
 * @param {number} quality 
 * @returns {Promise<File|null>}
 */
export async function convertHeicToJpeg(file, quality = 0.85) {
  if (!file) return null;
  try {
    const heic2anyModule = await import(/* webpackIgnore: true */ 'heic2any');
    const heic2any = heic2anyModule.default || heic2anyModule;
    const resultBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: quality,
    });
    const blob = Array.isArray(resultBlob) ? resultBlob[0] : resultBlob;
    const baseName = (file.name || 'image').replace(/\.[^/.]+$/, '').trim() || 'image';
    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (err) {
    console.warn('heic2any conversion fallback:', err);
    return null;
  }
}

/**
 * Loads an image from a File or Blob into an object with dimensions and a draw function.
 * @param {File|Blob} file 
 * @returns {Promise<{width: number, height: number, draw: Function}>}
 */
function loadImageSource(file) {
  return new Promise((resolve, reject) => {
    // 1. Try createImageBitmap if supported (fast, handles EXIF orientation)
    if (typeof window !== 'undefined' && typeof window.createImageBitmap === 'function') {
      window.createImageBitmap(file, { imageOrientation: 'from-image' })
        .then((bitmap) => {
          resolve({
            width: bitmap.width,
            height: bitmap.height,
            draw(ctx, w, h) {
              ctx.drawImage(bitmap, 0, 0, w, h);
              try { bitmap.close(); } catch (e) { /* ignore */ }
            },
          });
        })
        .catch(() => {
          // Fallback to HTMLImageElement
          loadImageViaElement(file).then(resolve).catch(reject);
        });
    } else {
      loadImageViaElement(file).then(resolve).catch(reject);
    }
  });
}

function loadImageViaElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        draw(ctx, w, h) {
          ctx.drawImage(img, 0, 0, w, h);
        },
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // Fallback using FileReader
      const reader = new FileReader();
      reader.onload = (e) => {
        const fallbackImg = new Image();
        fallbackImg.onload = () => {
          resolve({
            width: fallbackImg.naturalWidth || fallbackImg.width,
            height: fallbackImg.naturalHeight || fallbackImg.height,
            draw(ctx, w, h) {
              ctx.drawImage(fallbackImg, 0, 0, w, h);
            },
          });
        };
        fallbackImg.onerror = (err) => reject(new Error('Cannot decode image file.'));
        fallbackImg.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Cannot read image file.'));
      reader.readAsDataURL(file);
    };

    img.src = url;
  });
}

/**
 * Converts a Canvas to a Blob as a Promise.
 */
function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

/**
 * Automatically compress and resize any image file to ensure its size <= 1MB (or custom maxSizeBytes).
 * Converts to JPEG (or preserves SVG/GIF if already <= 1MB).
 * 
 * @param {File|Blob} file The input file.
 * @param {Object} [options]
 * @param {number} [options.maxSizeBytes=1048576] Maximum size in bytes (default: 1MB = 1048576).
 * @param {number} [options.maxInitialDimension=1920] Maximum width/height for initial scale.
 * @param {number} [options.initialQuality=0.85] Initial compression quality.
 * @returns {Promise<File|Blob>} Compressed File or Blob strictly <= maxSizeBytes.
 */
export async function compressImageToMax1MB(file, options = {}) {
  if (!file) return null;

  const maxSizeBytes = options.maxSizeBytes || MAX_IMAGE_SIZE_BYTES;
  const maxInitialDimension = options.maxInitialDimension || 1920;
  const initialQuality = options.initialQuality || 0.85;

  let workingFile = file;

  // 1. If HEIC / HEIF, convert to standard JPEG first so all browsers can render it as an image
  if (isHeicFile(file)) {
    try {
      const convertedJpeg = await convertHeicToJpeg(file, initialQuality);
      if (convertedJpeg) {
        workingFile = convertedJpeg;
      }
    } catch (err) {
      console.warn('HEIC to JPEG conversion failed, proceeding with original:', err);
    }
  }

  const fileName = workingFile.name || 'image.jpg';
  const fileExt = fileName.split('.').pop().toLowerCase();
  const fileMime = workingFile.type || '';

  // SVG files are vector XML; compressing to raster ruins infinite scalability.
  // If SVG is already under limit, keep as-is.
  if ((fileMime === 'image/svg+xml' || fileExt === 'svg') && workingFile.size <= maxSizeBytes) {
    return workingFile;
  }

  // Animated GIF under 1MB can be kept as-is to preserve frames.
  if ((fileMime === 'image/gif' || fileExt === 'gif') && workingFile.size <= maxSizeBytes) {
    return workingFile;
  }

  // Standard web formats already <= 1MB (and not needing HEIC conversion) can be kept as-is
  const isStandardWebFormat = ['image/jpeg', 'image/png', 'image/webp'].includes(fileMime) ||
    ['jpg', 'jpeg', 'png', 'webp'].includes(fileExt);

  if (!isHeicFile(file) && isStandardWebFormat && workingFile.size <= maxSizeBytes) {
    return workingFile;
  }

  // If converted from HEIC and already <= maxSizeBytes, return it
  if (isHeicFile(file) && workingFile !== file && workingFile.size <= maxSizeBytes) {
    return workingFile;
  }

  // Otherwise, load and compress via Canvas to guarantee <= 1MB
  let imageSource;
  try {
    imageSource = await loadImageSource(workingFile);
  } catch (err) {
    // If decoding failed in browser:
    // Fall back to working file gracefully rather than throwing and blocking attachment!
    console.warn('Canvas decoding failed for image, using working file:', err);
    return workingFile;
  }

  let { width, height } = imageSource;
  let currentMaxDim = maxInitialDimension;

  // Scale down to initial max dimension if necessary
  if (width > currentMaxDim || height > currentMaxDim) {
    if (width > height) {
      height = Math.round((height * currentMaxDim) / width);
      width = currentMaxDim;
    } else {
      width = Math.round((width * currentMaxDim) / height);
      height = currentMaxDim;
    }
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: false });

  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);

  // Fill canvas with white background in case of transparent PNG/WebP converted to JPEG
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  imageSource.draw(ctx, canvas.width, canvas.height);

  let quality = initialQuality;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);

  // Iterative quality and dimension reduction to strictly enforce <= maxSizeBytes
  let attempts = 0;
  const maxAttempts = 8;

  while (blob && blob.size > maxSizeBytes && attempts < maxAttempts) {
    attempts++;

    if (quality > 0.65) {
      quality = 0.65;
    } else if (quality > 0.50) {
      quality = 0.50;
    } else {
      // Scale down canvas dimensions geometrically based on size excess
      const ratio = Math.max(0.6, Math.min(0.85, Math.sqrt(maxSizeBytes / blob.size)));
      width = Math.max(200, Math.round(width * ratio));
      height = Math.max(200, Math.round(height * ratio));

      canvas.width = width;
      canvas.height = height;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      imageSource.draw(ctx, width, height);

      quality = 0.75;
    }

    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }

  if (!blob) {
    return file;
  }

  // Create clean File with .jpg extension
  const baseName = fileName.replace(/\.[^/.]+$/, '').trim() || 'image';
  const outputFileName = `${baseName}.jpg`;

  let finalFile;
  try {
    finalFile = new File([blob], outputFileName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (e) {
    // Fallback for older browsers without File constructor
    blob.name = outputFileName;
    blob.lastModifiedDate = new Date();
    finalFile = blob;
  }

  const originalMb = (file.size / (1024 * 1024)).toFixed(2);
  const finalKb = (finalFile.size / 1024).toFixed(1);
  console.log(`%c[SmartFix ImageCompressor] %cOriginal: ${originalMb} MB ➔ Compressed: ${finalKb} KB (Max 1MB: ${finalFile.size <= maxSizeBytes ? 'PASS' : 'FAIL'})`, 'color: #2563eb; font-weight: bold;', 'color: #059669; font-weight: bold;');

  return finalFile;
}

export default {
  MAX_IMAGE_SIZE_BYTES,
  SUPPORTED_IMAGE_EXTENSIONS,
  ACCEPT_ALL_IMAGES,
  isImageFile,
  isHeicFile,
  convertHeicToJpeg,
  compressImageToMax1MB,
};
